import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Wrench, Car, Sparkles, ArrowRight, Check, Clock, Phone, MessageCircle, Star, Info, ChevronUp, Zap, Gift } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_47615226-8be8-492e-9b64-c74bd12fbe55/artifacts/a0f8zaxp_02_MA_Africa_Detailing_Full_Colour_Transparent.webp";

const rooms = [
  { id: 'fix', title: 'FIX', subtitle: 'Mechanical', path: '/fix', icon: Wrench },
  { id: 'restore', title: 'RESTORE', subtitle: 'Panel Beating', path: '/restore', icon: Car },
  { id: 'detail', title: 'DETAIL', subtitle: 'Detailing', path: '/detail', icon: Sparkles },
];

// Tier badge styling
const tierStyles = {
  essential: { bg: 'bg-zinc-800', text: 'text-zinc-300', label: 'Essential' },
  popular: { bg: 'bg-blue-600', text: 'text-white', label: 'Most Popular' },
  premium: { bg: 'bg-[#C41E3A]', text: 'text-white', label: 'Premium' }
};

function RoomSwitcher({ currentRoom }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const currentRoomData = rooms.find(r => r.id === currentRoom);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
        data-testid="room-switcher"
      >
        {currentRoomData && <currentRoomData.icon className="w-4 h-4 text-[#C41E3A]" />}
        <span className="text-sm font-medium text-white">{currentRoomData?.title}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full left-0 mt-2 w-48 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl overflow-hidden z-50"
          >
            {rooms.filter(r => r.id !== currentRoom).map((room) => (
              <button
                key={room.id}
                onClick={() => {
                  navigate(room.path);
                  setIsOpen(false);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors"
              >
                <room.icon className="w-4 h-4 text-zinc-400" />
                <div className="text-left">
                  <p className="text-sm font-medium text-white">{room.title}</p>
                  <p className="text-xs text-zinc-500">{room.subtitle}</p>
                </div>
              </button>
            ))}
            <div className="border-t border-zinc-800">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-xs text-zinc-500"
                onClick={() => setIsOpen(false)}
              >
                ← Back to all services
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function DetailRoom() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('standard');
  const [expandedPackage, setExpandedPackage] = useState(null);
  const [loyaltyStatus, setLoyaltyStatus] = useState(null);
  const whatsappNumber = '27760784050';

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    if (user) {
      fetchLoyaltyStatus();
    }
  }, [user]);

  const fetchLoyaltyStatus = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/status`, { withCredentials: true });
      setLoyaltyStatus(response.data);
    } catch (error) {
      console.error('Error fetching loyalty status:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const [packagesRes, addonsRes] = await Promise.all([
        axios.get(`${API}/services/packages`),
        axios.get(`${API}/services/addons`)
      ]);
      setPackages(packagesRes.data);
      setAddons(addonsRes.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPackages = packages.filter(pkg => pkg.vehicle_category === activeCategory);
  const popularPackage = filteredPackages.find(pkg => pkg.tier === 'popular');

  // Full page loading state
  if (loading) {
    return (
      <main className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-zinc-900 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-8 h-8 text-[#C41E3A] animate-pulse" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Loading Detailing Services</h2>
          <p className="text-sm text-zinc-500">Please wait...</p>
          <div className="mt-6 flex justify-center gap-1">
            <motion.div
              className="w-2 h-2 bg-[#C41E3A] rounded-full"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
            />
            <motion.div
              className="w-2 h-2 bg-[#C41E3A] rounded-full"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.15 }}
            />
            <motion.div
              className="w-2 h-2 bg-[#C41E3A] rounded-full"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.3 }}
            />
          </div>
        </motion.div>
      </main>
    );
  }

  return (
    <main data-testid="detail-room" className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-900">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/">
              <img src={LOGO_URL} alt="MA Africa" className="h-8 md:h-10" />
            </Link>
            <RoomSwitcher currentRoom="detail" />
          </div>
          
          <div className="flex items-center gap-3">
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi MA Africa, I\'d like to enquire about detailing services')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <Link
              to="/booking"
              className="bg-[#C41E3A] text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-[#a01830] transition-colors"
            >
              Book Now
            </Link>
          </div>
        </div>
      </header>

      {/* Loyalty Program Banner */}
      {user && loyaltyStatus && (
        <section className="pt-24 pb-0 px-6">
          <div className="max-w-[1100px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-5 md:p-6 border ${
                loyaltyStatus.has_reward_available 
                  ? 'bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-zinc-900 border-amber-500/30' 
                  : 'bg-zinc-900/50 border-zinc-800'
              }`}
              data-testid="loyalty-banner"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    loyaltyStatus.has_reward_available ? 'bg-amber-500' : 'bg-zinc-800'
                  }`}>
                    {loyaltyStatus.has_reward_available ? (
                      <Gift className="w-6 h-6 text-white" />
                    ) : (
                      <Star className="w-6 h-6 text-[#C41E3A]" />
                    )}
                  </div>
                  <div>
                    {loyaltyStatus.has_reward_available ? (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Reward Available!</span>
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-white">
                          FREE {loyaltyStatus.reward_package.name}
                        </h3>
                        <p className="text-xs text-zinc-400 mt-1">
                          Worth R{loyaltyStatus.reward_package.value} - Select any package to redeem!
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Loyalty Program</span>
                        </div>
                        <h3 className="text-base md:text-lg font-semibold text-white">
                          {loyaltyStatus.bookings_until_reward} more booking{loyaltyStatus.bookings_until_reward !== 1 ? 's' : ''} until FREE Full Valet!
                        </h3>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="flex-1 max-w-[200px] h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#C41E3A] rounded-full transition-all"
                              style={{ width: `${(loyaltyStatus.current_progress / 5) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs font-medium text-zinc-500">
                            {loyaltyStatus.current_progress}/5
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Express Book Banner */}
      {popularPackage && (
        <section className={`${user && loyaltyStatus ? 'pt-4' : 'pt-24'} pb-0 px-6`}>
          <div className="max-w-[1100px] mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-blue-600/20 via-blue-500/10 to-zinc-900 border border-blue-500/30 rounded-2xl p-5 md:p-6"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Most Popular</span>
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-white">
                      {popularPackage.name} <span className="text-blue-400">R{popularPackage.base_price}</span>
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1 hidden md:block">
                      {popularPackage.description}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/booking?package=${popularPackage.id}&category=${popularPackage.vehicle_category}`}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="book-popular"
                >
                  <ArrowRight className="w-4 h-4" />
                  Book Now
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Hero */}
      <section className="pt-8 pb-12 px-6">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-zinc-900 px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-xs text-zinc-400 uppercase tracking-wider">Detailing Services</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-4">
              DETAIL
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-xl mx-auto mb-8">
              Professional interior and exterior detailing designed to restore, protect and maintain your vehicle.
            </p>

            {/* Vehicle Category Toggle */}
            <div className="inline-flex bg-zinc-900 rounded-xl p-1">
              <button
                onClick={() => setActiveCategory('standard')}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === 'standard'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Standard Vehicles
              </button>
              <button
                onClick={() => setActiveCategory('large')}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === 'large'
                    ? 'bg-[#C41E3A] text-white'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Large Vehicles
              </button>
            </div>
            <p className="text-xs text-zinc-600 mt-3">
              {activeCategory === 'standard' 
                ? 'Hatchback, Sedan, Coupe, SUV, Double Cab'
                : '7-Seater, Minibus, Van, Taxi, Commercial'
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages */}
      <section className="pb-16 px-6">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredPackages.map((pkg, index) => {
              const tier = tierStyles[pkg.tier] || tierStyles.essential;
              const isExpanded = expandedPackage === pkg.id;
              
              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-zinc-900 rounded-2xl overflow-hidden border transition-all hover:border-zinc-700 ${
                    pkg.tier === 'popular' ? 'border-blue-500 ring-1 ring-blue-500/20' : 'border-zinc-800'
                  }`}
                >
                  {/* Header */}
                  <div className={`px-6 py-4 ${pkg.tier === 'popular' ? 'bg-blue-500/10' : 'bg-zinc-800/50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${tier.bg} ${tier.text}`}>
                        {tier.label}
                      </span>
                      {pkg.tier === 'popular' && (
                        <Star className="w-4 h-4 text-blue-400 fill-blue-400" />
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-white">{pkg.name}</h3>
                  </div>
                  
                  {/* Price */}
                  <div className="px-6 py-5 border-b border-zinc-800">
                    <div className="flex items-end gap-1">
                      <span className="text-4xl font-bold text-white">R{pkg.base_price.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm text-zinc-500">
                      <Clock className="w-4 h-4" />
                      {pkg.duration}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="px-6 py-5">
                    <p className="text-sm text-zinc-400 mb-4">{pkg.description}</p>
                    
                    {pkg.best_for && (
                      <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
                        <p className="text-xs font-semibold text-zinc-300">Best for:</p>
                        <p className="text-xs text-zinc-500">{pkg.best_for}</p>
                      </div>
                    )}
                    
                    {pkg.result && (
                      <div className="mb-4">
                        <p className="text-xs font-semibold text-zinc-300">Result:</p>
                        <p className="text-xs text-zinc-500">{pkg.result}</p>
                      </div>
                    )}
                    
                    {/* What's Included Toggle */}
                    <button
                      onClick={() => setExpandedPackage(isExpanded ? null : pkg.id)}
                      className="flex items-center gap-1 text-xs font-medium text-[#C41E3A] hover:text-[#e63a54] mb-4"
                    >
                      <Info className="w-3 h-3" />
                      {isExpanded ? 'Hide details' : "What's included"}
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    
                    <AnimatePresence>
                      {isExpanded && pkg.includes && (
                        <motion.ul
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="space-y-2 mb-4 overflow-hidden"
                        >
                          {pkg.includes.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                              <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                  
                  {/* CTA - Direct to Booking */}
                  <div className="px-6 pb-6">
                    <Link
                      to={`/booking?package=${pkg.id}&category=${pkg.vehicle_category}`}
                      className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
                        pkg.tier === 'popular'
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : pkg.tier === 'premium'
                            ? 'bg-[#C41E3A] text-white hover:bg-[#a01830]'
                            : 'bg-zinc-800 text-white hover:bg-zinc-700'
                      }`}
                      data-testid={`book-${pkg.id}`}
                    >
                      <ArrowRight className="w-4 h-4" />
                      Book Now
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="py-16 px-6 bg-zinc-900/50">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Enhance Your Service
            </h2>
            <p className="text-sm text-zinc-500">
              Add premium treatments to any package
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {addons.map((addon, index) => (
              <motion.div
                key={addon.id}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
              >
                <p className="text-sm font-semibold text-white mb-1">{addon.name}</p>
                {addon.description && (
                  <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{addon.description}</p>
                )}
                <p className="text-lg text-[#C41E3A] font-bold">+R{addon.price.toLocaleString()}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6">
        <div className="max-w-[600px] mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to restore your vehicle?
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            Book online or WhatsApp us for a custom quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/booking"
              className="bg-[#C41E3A] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Now
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi MA Africa, I\'d like to enquire about detailing services')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-zinc-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-zinc-700 transition-colors"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-8 px-6">
        <div className="max-w-[1100px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            © {new Date().getFullYear()} MA Africa Fix & Detail
          </p>
          <p className="text-xs text-zinc-600">
            FIX. RESTORE. DETAIL.
          </p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 p-4 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-900">
        <div className="flex gap-3">
          <a
            href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hi MA Africa, I\'d like to book a detailing service')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-3 rounded-xl text-sm font-medium text-center bg-zinc-800 text-white flex items-center justify-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          <Link
            to="/booking"
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-center bg-[#C41E3A] text-white flex items-center justify-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Quick Book
          </Link>
        </div>
      </div>
    </main>
  );
}
